import { useCallback, useRef, type PointerEvent as ReactPointerEvent } from "react";
import { startPointerDrag } from "@/lib/dnd";

export interface KanbanTouchDragOptions<Id extends string | number> {
  /** Перетаскивание разрешено (нет прав на edit → тач-drag выключен, как и HTML5-DnD). */
  enabled: boolean;
  /** Задержка long-press до старта перетаскивания, мс (по умолчанию 230). */
  longPressMs?: number;
  /** Сработал long-press — drag начался (подсветить исходную карточку, поставить стейт). */
  onStart?: (cardId: Id) => void;
  /** Палец над колонкой `[data-kanban-col]` (или null, если ни над одной). */
  onOverColumn?: (colKey: string | null) => void;
  /**
   * Отпустили палец над колонкой — выполнить перенос. Обработчик сам финализирует
   * визуальный стейт (hook НЕ зовёт onEnd при успешном drop, чтобы не сбросить
   * состояние до того, как onDrop его прочитает).
   */
  onDrop: (colKey: string, cardId: Id) => void;
  /** Завершение жеста БЕЗ переноса (отмена / отпустили мимо колонки) — сбросить стейт. */
  onEnd?: () => void;
}

/**
 * Тач-перетаскивание карточек канбана (планшет / телефон). Тонкая обёртка над
 * единым ядром жеста `startPointerDrag` (lib/dnd.tsx): здесь только маппинг
 * «колонка ← ключ карточки» и семантика «onEnd не зовём при успешном drop».
 * Само ядро — long-press-drag на Pointer Events без сторонних библиотек; мышь
 * идёт прежним HTML5-путём (pointerType mouse → return).
 *
 * Требования к разметке (те же классы, что у эталона «Логистика рейсов»):
 *   • на карточке — класс `.kanban-card` (по нему ищется корень для клона-призрака);
 *   • на колонке — атрибут `data-kanban-col="<ключ>"`.
 * Анимации/стили — в globals.css (.kanban-col / .kanban-card / .kanban-card-ghost,
 * псевдонимы .dnd-col / .dnd-card / .dnd-ghost), там же гашение под
 * prefers-reduced-motion.
 *
 * Возвращает стабильный обработчик `(e, cardId)` для `onPointerDown` карточки.
 */
export function useKanbanTouchDrag<Id extends string | number>(
  opts: KanbanTouchDragOptions<Id>,
) {
  // Свежие опции держим в ref, чтобы возвращаемый обработчик был стабилен: его
  // вешают на КАЖДУЮ карточку доски — незачем пересоздавать замыкания на каждый рендер.
  const ref = useRef(opts);
  ref.current = opts;

  return useCallback((e: ReactPointerEvent, cardId: Id) => {
    const o = ref.current;
    if (!o.enabled) return;

    // Успешный drop над колонкой → onDrop сам финализирует стейт, поэтому onEnd
    // от ядра в этом случае глушим (иначе сбросим состояние раньше onDrop).
    let dropped = false;
    startPointerDrag(e, {
      itemSelector: ".kanban-card",
      targetSelector: "[data-kanban-col]",
      targetAttr: "data-kanban-col",
      longPressMs: o.longPressMs ?? 230,
      ghostRotate: true,
      onStart: () => o.onStart?.(cardId),
      onOver: (ctx) => o.onOverColumn?.(ctx.targetKey),
      onDrop: (ctx) => {
        if (ctx.targetKey != null) {
          dropped = true;
          o.onDrop(ctx.targetKey, cardId);
        }
      },
      onEnd: () => { if (!dropped) o.onEnd?.(); },
    });
  }, []);
}
